/** Sign-in preferences that outlive a task-pane reload, shared by LoginButton and LogoutButton. */

/** Values unchanged from earlier builds, so an existing install keeps its remembered provider. */
export type LoginMethod = "microsoft" | "okta";

const LOGIN_METHOD_KEY = "loginMethod";
/** Sign-in names, used as `loginHint` so a silent retry cannot land on the wrong account. */
const LOGIN_HINT_KEY = "entraLoginHint";
const OKTA_LOGIN_HINT_KEY = "oktaLoginHint";
/** Set on an explicit sign-out, so the next reload does not sign the user straight back in. */
const SIGNED_OUT_KEY = "signedOut";

const readStoredValue = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.error("LocalStorage is not available:", error);
    return null;
  }
};

const writeStoredValue = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    console.error("LocalStorage is not available:", error);
  }
};

const removeStoredValue = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error("LocalStorage is not available:", error);
  }
};

export function getLoginMethod(): LoginMethod | null {
  const value = readStoredValue(LOGIN_METHOD_KEY);
  return value === "microsoft" || value === "okta" ? value : null;
}

export function rememberLoginMethod(method: LoginMethod): void {
  writeStoredValue(LOGIN_METHOD_KEY, method);
}

export function getEntraLoginHint(): string | undefined {
  return readStoredValue(LOGIN_HINT_KEY) || undefined;
}

export function rememberEntraLoginHint(username: string): void {
  if (username) {
    writeStoredValue(LOGIN_HINT_KEY, username);
  }
}

export function getOktaLoginHint(): string | undefined {
  return readStoredValue(OKTA_LOGIN_HINT_KEY) || undefined;
}

export function rememberOktaLoginHint(username: string): void {
  if (username) {
    writeStoredValue(OKTA_LOGIN_HINT_KEY, username);
  }
}

/**
 * The hints are email addresses, so an explicit sign-out drops them rather than priming the next
 * sign-in with the previous user. The remembered provider deliberately survives.
 */
export function forgetSignInHints(): void {
  removeStoredValue(LOGIN_HINT_KEY);
  removeStoredValue(OKTA_LOGIN_HINT_KEY);
}

export function isExplicitlySignedOut(): boolean {
  return readStoredValue(SIGNED_OUT_KEY) === "true";
}

export function markExplicitlySignedOut(): void {
  writeStoredValue(SIGNED_OUT_KEY, "true");
}

export function clearExplicitSignOut(): void {
  removeStoredValue(SIGNED_OUT_KEY);
}
